import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import { AccountDisabledBadge } from '@/pages/dashboard/drivers/components/DriverStatusBadges';
import { fetchDriverReviews, type DriverReviewsResult } from '@/services/reviewsService';
import ReviewCard from '../components/ReviewCard';
import StarsRating from '../components/StarsRating';

export default function DriverReviewsDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { driverId = '' } = useParams<{ driverId: string }>();

  const [data, setData] = useState<DriverReviewsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!driverId) return;
    setLoading(true);
    setError(false);
    setNotFound(false);
    try {
      setData(await fetchDriverReviews(driverId));
    } catch (err) {
      if (err instanceof Error && err.message === 'driver-not-found') setNotFound(true);
      else setError(true);
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    load();
  }, [load]);

  const backLink = (
    <Link
      to="/dashboard/reviews?tab=drivers"
      className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 transition-colors hover:text-foreground-900"
    >
      <i className="ri-arrow-left-line text-lg rtl:rotate-180" aria-hidden="true" />
      {t('reviews.detail.back')}
    </Link>
  );

  if (loading) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card className="space-y-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-56" />
            </div>
          </div>
          <Skeleton className="h-24 w-full" />
        </Card>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <EmptyState
            icon="ri-user-unfollow-line"
            title={t('reviews.notFound')}
            action={
              <Button variant="primary" size="md" onClick={() => navigate('/dashboard/reviews?tab=drivers')}>
                {t('reviews.notFoundBack')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <ErrorState
            title={t('reviews.error.detail')}
            description={t('common.errorText')}
            retryLabel={t('common.retry')}
            onRetry={load}
          />
        </Card>
      </div>
    );
  }

  const { driver, summary, reviews } = data;

  return (
    <div className="space-y-5">
      {backLink}

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-500 text-xl font-bold text-background-50">
              {driver.avatarUrl ? (
                <img src={driver.avatarUrl} alt={driver.fullName} className="h-full w-full object-cover" />
              ) : (
                driver.fullName.charAt(0)
              )}
            </span>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-foreground-950 md:text-xl">
                {t('reviews.detail.title', { name: driver.fullName })}
              </h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="lb-ltr-field text-xs text-foreground-400">{driver.id}</span>
                {driver.accountDisabled ? <AccountDisabledBadge /> : null}
              </div>
            </div>
          </div>

          <div className="text-end">
            {summary.average === null ? (
              <>
                <StarsRating value={0} size="lg" />
                <p className="mt-1 text-xs font-medium text-foreground-500">{t('reviews.overall.none')}</p>
              </>
            ) : (
              <>
                <div className="flex items-end justify-end gap-1.5">
                  <span className="text-3xl font-bold leading-none text-foreground-950">
                    {summary.average.toFixed(1)}
                  </span>
                  <span className="text-sm text-foreground-400">{t('reviews.overall.outOf')}</span>
                </div>
                <StarsRating value={summary.average} size="md" className="mt-1.5" />
                <p className="mt-1 text-xs text-foreground-500">
                  {t('reviews.drivers.count')}: {summary.count}
                </p>
              </>
            )}
          </div>
        </div>
      </Card>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-foreground-950">{t('reviews.list.title')}</h2>
        {reviews.length === 0 ? (
          <EmptyState
            icon="ri-star-line"
            title={t('reviews.detail.empty')}
            description={t('reviews.detail.emptyDesc')}
          />
        ) : (
          <div className="space-y-3">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        )}
      </section>

      <p className="flex items-start gap-2 px-1 text-xs leading-relaxed text-foreground-500">
        <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
        {t('reviews.note')}
      </p>
    </div>
  );
}